import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Table2 } from 'lucide-react';
import { api } from '../services/api';
import { SheetCompare as Data } from '../types';
import { useData } from '../App';

const fmt = (n: number | null | undefined) => n === null || n === undefined ? '—'
  : new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// Блок «Как в таблице»: строки шапки листа «ПриходыЗП» — значения приложения рядом со значениями таблицы «Мани»
export const SheetCompare: React.FC<{ year: number; scope: string }> = ({ year, scope }) => {
  const { revision, showNotification } = useData();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Data | null>(null);
  useEffect(() => {
    if (open) api.sheetCompare(year).then(setData).catch(() => setData(null));
  }, [open, year, revision]);
  const col = scope && scope !== 'all' ? scope : 'all';
  const save = async (rowKey: string, patch: { value?: number | null; note?: string | null }) => {
    try { setData(await api.saveSheetCompare({ year, organizationId: col, rowKey, ...patch })); }
    catch (e: any) { showNotification(e?.message || 'Не удалось сохранить', 'error'); }
  };
  return (
    <div className="bg-white rounded-lg border border-slate-200 text-xs">
      <button className="w-full flex items-center gap-2 px-3 py-2 font-semibold text-slate-700" onClick={() => setOpen(o => !o)}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}<Table2 size={14} /> Как в таблице «Мани» — {year}
        {data && <span className="font-normal text-slate-400">· {data.columns.find(c => c.id === col)?.name}</span>}
      </button>
      {open && data && (
        <div className="overflow-x-auto px-3 pb-3">
          <table className="w-full min-w-[640px]">
            <thead className="text-slate-500"><tr className="border-b">
              <th className="text-left py-1 font-medium">Строка</th><th className="text-right font-medium">Приложение</th>
              <th className="text-right font-medium">Таблица</th><th className="text-right font-medium">Разница</th>
              <th className="text-left pl-3 font-medium">Причина</th></tr></thead>
            <tbody>
              {data.rows.map(r => {
                const c = r.cells[col];
                const bold = r.indent === 0;
                return (
                  <tr key={r.key} className="border-b border-slate-100 align-top">
                    <td className={`py-1 ${bold ? 'font-semibold' : r.indent > 1 ? 'pl-8 text-slate-500 italic' : 'pl-4 text-slate-600'}`}>{r.label}</td>
                    <td className="text-right whitespace-nowrap">{fmt(c?.app)}</td>
                    <td className="text-right">
                      <input key={`${r.key}-${col}-${c?.table}`} defaultValue={c?.table ?? ''} inputMode="decimal"
                        className="w-28 text-right border border-transparent hover:border-slate-200 focus:border-blue-400 rounded px-1"
                        onBlur={e => {
                          const raw = e.target.value.replace(/\s/g, '').replace(',', '.');
                          const v = raw === '' ? null : Number(raw);
                          if (v !== null && Number.isNaN(v)) return;
                          if (v !== (c?.table ?? null)) save(r.key, { value: v });
                        }} />
                    </td>
                    <td className={`text-right whitespace-nowrap ${c?.diff && Math.abs(c.diff) >= 1 ? 'text-amber-700' : 'text-slate-400'}`}>{fmt(c?.diff)}</td>
                    <td className="pl-3 text-slate-500 min-w-[220px]">
                      {r.reason && <div>{r.reason}</div>}
                      <input key={`n-${r.key}-${col}-${c?.note}`} defaultValue={c?.note ?? ''} placeholder="своя причина…"
                        className="w-full border border-transparent hover:border-slate-200 focus:border-blue-400 rounded px-1 text-slate-700"
                        onBlur={e => { if ((e.target.value || null) !== (c?.note ?? null)) save(r.key, { note: e.target.value || null }); }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-1">«Таблица» — значения шапки листа «ПриходыЗП» (строки 7–25 и остаток строки 31), импорт переноса; можно поправить вручную.
            Приложение считает по формулам таблицы из операций года. Переводы между своими счетами и в кошелёк партнёра — не приход и не расход, здесь не учитываются.</p>
        </div>
      )}
    </div>
  );
};
